<i18n locale="en">
{
  "titles" : {
    "add": "Add Existing Page",
    "update": "Update Page"
  },
  "actions": {
    "update": "Update",
    "add": "Add"
  },
  "labels": {
    "custom-label": "Custom Label (optional)",
    "custom-label-placeholder": "Aa"
  },
  "errors": {
    "load-pages": "Unable to load pages"
  }
}
</i18n>

<template>
  <OwnForm :submit-handler="onUpdate">
    <OwnDialog :model-value="true" class="add-link-dialog" @cancel="close">
      <template #heading>
        <OwnDialogHeading
          :title="editItem ? t('titles.update') : t('titles.add')"
          @cancel="close"
        />
      </template>

      <OwnDialogContent scroll>
        <FullScreenLoader v-if="isLoading" />
        <div v-else class="flex-col gap-4">
          <OwnTouchRadioGroup
            v-model="selectedOption"
            :options="options"
            :columns="2"
          />
          <OwnInputContainer :label="t('labels.custom-label')">
            <OwnInput
              v-model="customLabel"
              :placeholder="t('labels.custom-label-placeholder')"
            />
          </OwnInputContainer>
        </div>
      </OwnDialogContent>

      <template #footer>
        <OwnDialogFooter>
          <OwnButton
            :text="editItem ? t('actions.update') : t('actions.add')"
            variant="primary"
            type="submit"
          >
            <template v-if="!editItem" #icon-right>
              <PhPlusCircle size="24" />
            </template>
          </OwnButton>
        </OwnDialogFooter>
      </template>
    </OwnDialog>
  </OwnForm>
</template>

<script>
import { PhPlusCircle } from '@phosphor-icons/vue'
import { useI18n } from 'vue-i18n'

import FullScreenLoader from '@/components/loaders/FullScreenLoader.vue'
import { useApplicationScope } from '@/core/store'
import { logError } from '@/logger'
import notify from '@/mixins/notify'
import { OlympusClient } from '@/OlympusClient'
import { OwnButton } from '@/ui/OwnButton'
import {
  OwnDialog,
  OwnDialogContent,
  OwnDialogFooter,
  OwnDialogHeading,
} from '@/ui/OwnDialog'
import { OwnForm } from '@/ui/OwnForm'
import { OwnInput } from '@/ui/OwnInput'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { OwnTouchRadioGroup } from '@/ui/OwnTouchRadioGroup'

export default {
  name: 'AddPageDialog',
  components: {
    FullScreenLoader,
    OwnButton,
    OwnDialog,
    OwnDialogContent,
    OwnDialogFooter,
    OwnDialogHeading,
    OwnForm,
    OwnInput,
    OwnInputContainer,
    OwnTouchRadioGroup,
    PhPlusCircle,
  },
  mixins: [notify],
  props: {
    editItem: { type: Object, default: undefined },
  },
  emits: ['update', 'close'],
  setup() {
    const { t } = useI18n()
    const { activeBrandId } = useApplicationScope()

    return { activeBrandId, t }
  },
  data() {
    return {
      customLabel: '',
      isLoading: false,
      options: [],
      selectedOption: undefined,
    }
  },
  async created() {
    const { editItem } = this

    await this.loadPageOptions()

    if (editItem) {
      this.selectedOption = editItem.params.pageId
      // Populate custom label if it differs from the default page title
      const pageOption = this.options.find(
        (option) => option.value === editItem.params.pageId
      )
      if (pageOption && editItem.params.label !== pageOption.label) {
        this.customLabel = editItem.params.label || ''
      }
    } else if (this.options.length > 0) {
      this.selectedOption = this.options[0].value
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    async loadPageOptions() {
      const { activeBrandId } = this

      try {
        this.isLoading = true

        const { results } = await OlympusClient.funnel.v1.pages.getPages({
          query: {
            brandId: activeBrandId,
            limit: 50,
          },
        })

        this.options = results.map((page) => ({
          label: page.title,
          value: page.id,
        }))
      } catch (err) {
        logError(err)
        this.$notify(this.t('errors.load-pages'), 'error')
      } finally {
        this.isLoading = false
      }
    },
    onUpdate() {
      const { options, selectedOption, customLabel } = this

      const fullSelectedOption = options.find(
        (option) => option.value === selectedOption
      )

      this.$emit('update', {
        params: {
          label: customLabel || fullSelectedOption.label,
          pageId: fullSelectedOption.value,
        },
        type: 'page',
      })

      this.close()
    },
  },
}
</script>
